import { useRef, useEffect, useLayoutEffect, useState, useCallback, useMemo } from "react"
import { useAuth } from "@/hooks/useAuth"
import { useLoginModal } from "@/contexts/LoginModalContext"
import { useSubscription } from "@/contexts/SubscriptionContext"; // Added
import { usePurchaseState } from "@/hooks/usePurchaseState"
import { useMessageTemplates } from "@/hooks/useMessageTemplates"
import { useCustomerTemplates } from "@/hooks/useCustomerTemplates"
import { useOrdersByDate } from "@/hooks/useOrdersByDate"
import { useCustomerItemOperations } from "@/hooks/useCustomerItemOperations"
import { useMoveOrderDate } from "@/hooks/useMoveOrderDate"
import { useShippingFeeMode } from "@/hooks/useShippingFeeMode"
import { useOrderStatusReconcile } from "@/hooks/useOrderStatusReconcile"
import { useOrderKeepActions } from "@/hooks/useOrderKeepActions"
import { useSameProductBulkEdit } from "@/hooks/useSameProductBulkEdit"
import { useCustomerCrud } from "@/hooks/useCustomerCrud"
import { useOrderStatusActions } from "@/hooks/useOrderStatusActions"
import { useMemberPreference } from "@/hooks/useMemberPreference";
import { usePaymentMethodActions } from "@/hooks/usePaymentMethodActions";
import { useOrderSummaries } from "@/hooks/useOrderSummaries"
import { useShippingSnapshotNudge } from "@/hooks/useShippingSnapshotNudge"
import { useProductCatalog } from "@/hooks/useProductCatalog";
import { useInventoryQuickAdd } from "@/hooks/useInventoryQuickAdd";
import { isPaidPlanId } from "@/lib/planAccess";
import { useBlacklist } from "@/hooks/useBlacklist";
import { CustomerTemplateManager, CustomerTemplate } from "@/components/CustomerTemplateManager"
import { OrderDateNotice } from "@/components/OrderDateNotice"
import { DepositIncomingProvider } from "@/contexts/DepositIncomingContext"
import { DepositFallbackCards } from "@/components/deposits/DepositFallbackCards"
import { ActiveCustomerColumn, type ActiveCustomerColumnVariant } from "@/components/ActiveCustomerColumn"
import { SettingsManager } from "@/components/SettingsManager"
import { TooltipProvider } from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Item, NewItem } from "@/types/purchase";
import { RelevantOrdersPanelContainer } from "@/components/RelevantOrdersPanelContainer";
import { ViewModeToggle } from "@/components/ViewModeToggle";
import { MessageHeaderToolbar } from "@/components/MessageHeaderToolbar";
import { LgMessageHeaderToolbar } from "@/components/LgMessageHeaderToolbar";
import { MessageTemplateColumn } from "@/components/MessageTemplateColumn";
import { MemoViewerDialog } from "@/components/MemoViewerDialog";
import { DeleteConfirmDialog } from "@/components/DeleteConfirmDialog";
import { MoveOrderDateDialog } from "@/components/MoveOrderDateDialog";
import { useHomeUrlParams } from "@/hooks/useHomeUrlParams";
import { Loader2 } from "lucide-react"
import * as orderService from "@/supabase/order/service/orderService";
import { toast } from "sonner";
import { logError } from "@/lib/sentry";
import { OrderStatus, selectableOrderStatuses } from "@/components/OrderControls";
import { useDepositAccountStatus } from "@/hooks/useDepositAccountStatus";
import { CombinedShippingModalContainer, CombinedShippingModalHandle } from "@/components/CombinedShippingModalContainer";
import { ConfirmDialogContainer, ConfirmDialogHandle } from "@/components/ConfirmDialogContainer";
import { TotalOrderSummary } from "@/components/TotalOrderSummary";
import { BatchModeView } from "@/components/BatchModeView";
import { ShippingModalManager, ShippingModalHandle } from "@/components/ShippingModalManager";
import { useOnboarding } from "@/hooks/useOnboarding"
import { useMediaQuery } from "@/hooks/useMediaQuery"
import { OnboardingTour } from "@/components/onboarding/OnboardingTour"

const DEFAULT_CUSTOMER_NICKNAME = "고객 닉네임"
const DEFAULT_CUSTOMER_STATIC_ID = "default_customer_placeholder_id"; // 고정 ID

// 빈 items 배열의 안정 identity (derive 시 매 렌더 새 [] 생성으로 인한 churn 방지).
const EMPTY_ITEMS: Item[] = [];
const getItemsTotalPrice = (items: Item[]) =>
  items.reduce((sum, item) => sum + item.price * item.quantity, 0);
// items prop은 배열 또는 (prev=>next) 업데이터 양형. prev 기준으로 resolve.
const resolveItems = (
  itemsOrUpdater: Item[] | ((prev: Item[]) => Item[]),
  prev: Item[]
): Item[] => (typeof itemsOrUpdater === "function" ? itemsOrUpdater(prev) : itemsOrUpdater);

// items 값비교 ({name,price,quantity}). 참조비교 금지 — refreshOrders가 매번 새 객체를
// 만들어 참조는 항상 다르므로(=bail 무력화), 값으로 비교해야 한다.
const itemsValueEqual = (a: Item[], b: Item[]): boolean => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].name !== b[i].name || a[i].price !== b[i].price || a[i].quantity !== b[i].quantity) {
      return false;
    }
  }
  return true;
};

// merge effect bail-out용 구조적 동등 비교. 같으면 prev 참조를 그대로 반환해 React가
// 2번째 wave 렌더를 건너뛰게 한다. (A4 설계 §10.5)
// - 정확한 id 순서까지 비교(첫 고객이 active fallback 결정).
// - id-only 비교 금지(같은 id·다른 필드 서버변경 누락 = stale UI).
const orderTemplatesEqual = (a: CustomerTemplate[], b: CustomerTemplate[]): boolean => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (
      x.id !== y.id ||
      x.nickname !== y.nickname ||
      x.template !== y.template ||
      x.is_keep !== y.is_keep ||
      x.status !== y.status ||
      (x.shippingFeeOverride ?? null) !== (y.shippingFeeOverride ?? null) ||
      (x.paymentMethod ?? null) !== (y.paymentMethod ?? null) ||
      (x.memo ?? "") !== (y.memo ?? "") ||
      x.createdAt !== y.createdAt ||
      x.totalPrice !== y.totalPrice ||
      x.order_date !== y.order_date
    ) {
      return false;
    }
    if (!itemsValueEqual(x.items ?? EMPTY_ITEMS, y.items ?? EMPTY_ITEMS)) return false;
  }
  return true;
};

export default function PurchaseConfirmation() {
  const { user, pseudoUserId, loading: isAuthLoading } = useAuth()
  const { subscription, subscriptionPlan, isLoading: isSubLoading } = useSubscription(); // Added subscription, isLoading

  // 온보딩 투어 활성화 조건: 비로그인(게스트) 또는 로그인 상태인 경우 체험판(is_trial)일 때만
  // 유료 구독자나 체험판 종료된 유저는 온보딩 제외
  const isTourEnabled = useMemo(() => {
    if (!user) return true;
    return subscription?.is_trial === true;
  }, [user, subscription]);

  const onboarding = useOnboarding({
    enabled: isTourEnabled,
    loading: !!user && isSubLoading
  });

  const { openLoginModal } = useLoginModal()
  const inlineMemoRef = useRef<HTMLTextAreaElement>(null);
  const popoverMemoRef = useRef<HTMLTextAreaElement>(null);
  const {
    productCatalog,
    addOrUpdateItemInCatalog,
    isLoading: isCatalogLoading,
    deleteItem: deleteCatalogItem,
    clearItems: clearCatalog,
  } = useProductCatalog(user?.id || pseudoUserId);

  // PR6 재고 quick-add: 게스트/실패/마스터 0개 = 둘 다 null → 아래 renderProductColumn prop이
  // null로 내려가 화면 무변화(fail-closed). 참조는 훅이 안정 보장이라 memo 계약 안전.
  // refreshQuickAdd = 담기 계열 액션 후 재조회(아래 래퍼들) — 클릭 즉시 뱃지 반영.
  const { masterItems: inventoryMasterItems, stockByNorm: inventoryStockByNorm, refreshQuickAdd } =
    // 무료 플랜은 재고 배지·재고 상품 바로 담기 off(훅의 fail-closed 경로 = 재고 미등록 셀러와 동일 화면).
    useInventoryQuickAdd(isPaidPlanId(subscription?.subscription_plan_id) ? user?.id : undefined);

  const isXl = useMediaQuery("(min-width: 1280px)");
  const isLg = useMediaQuery("(min-width: 1024px)");

  const [isMemoPopoverOpen, setIsMemoPopoverOpen] = useState(false);
  const [isMemoPopoverOpenCompact, setIsMemoPopoverOpenCompact] = useState(false);
  const [memoInput, setMemoInput] = useState("");
  const [isEditingMemo, setIsEditingMemo] = useState(false);

  useEffect(() => {
    setIsMemoPopoverOpen(false);
    setIsMemoPopoverOpenCompact(false);
  }, [isXl, isLg]);

  const [viewMode, setViewMode] = useState<'detail' | 'batch'>('detail');
  const handleViewModeChange = useCallback((mode: 'detail' | 'batch') => setViewMode(mode), []);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [keepFilter, setKeepFilter] = useState('all'); // 'all', 'kept', 'not-kept'
  const [memoToShow, setMemoToShow] = useState<{ nickname: string; memo: string } | null>(null);
  const [sortOption, setSortOption] = useState('id-desc');


  const shippingModalRef = useRef<ShippingModalHandle>(null);
  // 모달 오픈은 ref 호출만 → Home 상태를 건드리지 않아 Home 본체가 재렌더되지 않는다.
  const openShippingModal = useCallback((customer: CustomerTemplate) => {
    shippingModalRef.current?.open(customer);
  }, []);

  const {
    selectedDate,
    setSelectedDate,
    customerTemplates: initialDateOrderTemplates,
    loading: ordersLoading,
    refreshOrders,
    updateDefaultCustomerNickname,
    getDefaultCustomer,
    removeOrderAndUpdateTemplates,
    removeLocalCustomerOnly,
  } = useOrdersByDate()

  const [localDateOrderTemplates, _setLocalDateOrderTemplates] = useState<CustomerTemplate[]>(initialDateOrderTemplates);
  const setLocalDateOrderTemplates = useCallback((updater: CustomerTemplate[] | ((prev: CustomerTemplate[]) => CustomerTemplate[])) => {
    if (typeof updater === 'function') {
      _setLocalDateOrderTemplates(prev => {
        const newState = updater(prev);
        return newState;
      });
    } else {
      _setLocalDateOrderTemplates(updater);
    }
  }, []);

  // 비회원용 고객별 배송비 오버라이드 저장소 (usePurchaseState 이전에 선언)
  const [guestShippingOverrides, setGuestShippingOverrides] = useState<Record<string, number | null>>({});

  // 방금 로컬에서 생성(createEmptyOrder)했지만 아직 서버 재조회 결과(initialDateOrderTemplates)에
  // 반영되지 않은 order_ 고객 id 집합. in-flight/stale 조회가 늦게 도착해 이 주문을 누락한 채
  // emit하면 머지에서 통째로 삭제 → focus가 엉뚱한 고객으로 점프하는 버그가 있어, 보존용으로 추적.
  // 날짜가 바뀌면(다른 날짜 주문 집합이므로) 비운다.
  const pendingCreatedOrderIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    pendingCreatedOrderIdsRef.current = new Set();
  }, [selectedDate]);

  useEffect(() => {
    // 서버 스냅샷이 이미 반영한 주문은 보존 대상에서 제거 (이후 정상 삭제가 되살아나지 않도록).
    initialDateOrderTemplates.forEach(c => pendingCreatedOrderIdsRef.current.delete(c.id));
    setLocalDateOrderTemplates(prevLocalDateOrderTemplates => {
      const newTemplatesMap = new Map<string, CustomerTemplate>();
      // prev를 id→고객 Map으로 1회 인덱싱. 이후 .find(O(n)) 루프 → .get(O(1))으로 (전체 O(n²)→O(n)).
      const prevById = new Map(prevLocalDateOrderTemplates.map(c => [c.id, c]));

      // Step 1: Populate map from initialDateOrderTemplates (source of truth for items, status etc. from DB for the date)
      initialDateOrderTemplates.forEach(initialCust => {
        const prevVersion = prevById.get(initialCust.id);
        newTemplatesMap.set(initialCust.id, {
          ...initialCust,
          // Preserve shippingFeeOverride from previous local state if it existed, otherwise default to null
          shippingFeeOverride: prevVersion?.shippingFeeOverride ?? (initialCust as CustomerTemplate).shippingFeeOverride ?? null,
          memo: (prevVersion as any)?.memo ?? (initialCust as any)?.memo ?? '',
          paymentMethod: prevVersion?.paymentMethod ?? (initialCust as CustomerTemplate).paymentMethod ?? null,
        } as CustomerTemplate);
      });

      // Step 2: Add any temp_ customers from previous local state that are not in initialDateOrderTemplates
      prevLocalDateOrderTemplates.forEach(localCust => {
        if (localCust.id.startsWith('temp_') && !newTemplatesMap.has(localCust.id)) {
          newTemplatesMap.set(localCust.id, { // Ensure temp customers also carry their override
            ...localCust,
            shippingFeeOverride: localCust.shippingFeeOverride ?? null,
          } as CustomerTemplate);
        }
      });

      // Step 2.5: 방금 로컬 생성됐지만 아직 서버 조회에 안 잡힌 order_ 고객 보존.
      // prevLocal에 살아있는(=삭제되지 않은) pending order만 보존하므로, 삭제는 정상 반영됨
      // (삭제 시 prevLocal에서 먼저 제거되어 여기서 부활하지 않음).
      prevLocalDateOrderTemplates.forEach(localCust => {
        if (
          localCust.id.startsWith('order_') &&
          pendingCreatedOrderIdsRef.current.has(localCust.id) &&
          !newTemplatesMap.has(localCust.id)
        ) {
          newTemplatesMap.set(localCust.id, { ...localCust });
        }
      });

      const hasOrderCustomer = Array.from(newTemplatesMap.values()).some(c => c.id.startsWith('order_'));
      const staticDefaultInMap = newTemplatesMap.get(DEFAULT_CUSTOMER_STATIC_ID);
      const initialHasStaticDefault = initialDateOrderTemplates.some(c => c.id === DEFAULT_CUSTOMER_STATIC_ID);

      if (staticDefaultInMap) {
        if (hasOrderCustomer) {
          const evolvedDefault = Array.from(newTemplatesMap.values()).find(
            c => c.id.startsWith('order_') && c.nickname === staticDefaultInMap.nickname
          );
          if (evolvedDefault) {
            let itemsToKeep = evolvedDefault.items;
            if ((!evolvedDefault.items || evolvedDefault.items.length === 0) && staticDefaultInMap.items && staticDefaultInMap.items.length > 0) {
              itemsToKeep = staticDefaultInMap.items;
            }
            newTemplatesMap.set(evolvedDefault.id, { ...evolvedDefault, items: itemsToKeep });
          }
          newTemplatesMap.delete(DEFAULT_CUSTOMER_STATIC_ID);
        } else if (!initialHasStaticDefault && staticDefaultInMap.nickname !== DEFAULT_CUSTOMER_NICKNAME) {
        } else if (!initialHasStaticDefault && staticDefaultInMap.nickname === DEFAULT_CUSTOMER_NICKNAME) {
        }
      }

      Array.from(newTemplatesMap.values()).forEach(currentMapCust => {
        const prevLocalCust = prevById.get(currentMapCust.id);
        if (prevLocalCust && (!currentMapCust.items || currentMapCust.items.length === 0) &&
          prevLocalCust.items && prevLocalCust.items.length > 0) {
          newTemplatesMap.set(currentMapCust.id, { ...currentMapCust, items: prevLocalCust.items });
        }
      });

      prevLocalDateOrderTemplates.forEach(localCust => {
        if (localCust.id.startsWith('temp_')) {
          if (!newTemplatesMap.has(localCust.id) || newTemplatesMap.get(localCust.id) !== localCust) {
            newTemplatesMap.set(localCust.id, { ...localCust });
          }
        }
      });

      const finalTemplates = Array.from(newTemplatesMap.values());
      // merge 결과가 prev와 구조적으로 동일하면 prev 참조를 그대로 반환 → React bail-out으로
      // 중복 2번째 wave 렌더 제거. 틀려도 새 배열을 반환할 뿐이라 회귀 아님(no-regression-safe).
      // 비교는 반드시 finalTemplates 전체 계산 후에 수행(위 pending cleanup이 먼저 돌아야 함).
      if (orderTemplatesEqual(finalTemplates, prevLocalDateOrderTemplates)) {
        return prevLocalDateOrderTemplates;
      }
      return finalTemplates;
    });
  }, [initialDateOrderTemplates, setLocalDateOrderTemplates]);

  const {
    customerTemplates: sessionCustomerTemplates,
    setActiveCustomerId: setSessionActiveCustomerId,
    addCustomer: addSessionCustomer,
    removeCustomer: removeSessionCustomer,
    updateTemplate: updateSessionTemplate,
    updateCustomerItems: updateSessionCustomerItems,
    updateCustomerNickname: updateSessionCustomerNickname,
  } = useCustomerTemplates(user?.id, pseudoUserId)

  const [activeCustomerId, _setActiveCustomerIdState] = useState<string | null>(null);
  const setActiveCustomerIdState = useCallback((id: string | null) => {
    _setActiveCustomerIdState(id);
    if (!user && setSessionActiveCustomerId) {
      setSessionActiveCustomerId(id);
    }
  }, [user, setSessionActiveCustomerId]);

  const customerTemplates = user ? localDateOrderTemplates : sessionCustomerTemplates

  // 입금 폴백 카드(그리드에 행 없는 입금만). 안정 element — provider 재렌더에 무관.
  const depositIncomingSlot = useMemo(() => <DepositFallbackCards />, []);
  // 입금 칩이 붙을 수 있는 행 = 미지정·non-keep 주문만. 비-미지정(입금완료/출고완료/킵) 행에는
  // 칩을 띄우지 않고 그 매칭은 폴백 카드로 보낸다(색 행에 stale 칩/확정 방지 — codexloop R1).
  const gridOrderIds = useMemo(() => {
    const s = new Set<number>();
    for (const c of customerTemplates) {
      if (c.is_keep || c.status !== '미지정') continue;
      const m = c.id.match(/^order_(\d+)$/);
      if (m) s.add(Number(m[1]));
    }
    return s;
  }, [customerTemplates]);

  const activeCustomer = activeCustomerId
    ? customerTemplates.find(c => c.id === activeCustomerId) as CustomerTemplate | null | undefined // Explicit cast
    : null;

  const {
    ordersWithShippingInfo,
    shippingInfoByOrderId,
    filteredCustomerTemplates,
    totalOrderSummary,
    hasPaidOrders,
    handleDownloadSettlement,
    handleDownloadFilteredExcel,
    handleShippingInfoKnown,
  } = useOrderSummaries({
    customerTemplates,
    user,
    selectedDate,
    statusFilter,
    keepFilter,
    sortOption,
  });

  // 배송정보 상품명 스냅샷 stale 감지 (관찰 전용 — 항목 저장 경로 무변경)
  useShippingSnapshotNudge({
    user,
    activeCustomerId,
    activeCustomer: activeCustomer ?? null,
    customerTemplates,
    ordersWithShippingInfo,
  });

  // currentItems: 미러 state를 제거하고 activeCustomer.items에서 직접 derive.
  // activeCustomer는 로그인=localDateOrderTemplates / 비로그인=sessionCustomerTemplates를 읽으므로
  // 양쪽 source를 모두 반영. 모든 로컬 item 변경은 아래 handleItemsChangeAndPersist 하나로 흐른다.
  const currentItems = activeCustomer?.items ?? EMPTY_ITEMS;

  // 단일 chokepoint: active customer items의 *유일한* 로컬 source writer.
  // 로그인 = localDateOrderTemplates(source)를 함수형 updater 안에서 직접 갱신 + totalPrice 재계산.
  // 비로그인 = 세션 스토어. DB 영속은 이 함수 밖(각 핸들러의 service 호출)에서 유지.
  // 함수형 updater는 prev(cust.items) 기준으로 resolve → pure, StrictMode-safe, nested setState 없음.
  const handleItemsChangeAndPersist = useCallback((itemsOrUpdater: Item[] | ((prevItems: Item[]) => Item[])) => {
    if (!activeCustomerId) return;
    if (!user) {
      if (updateSessionCustomerItems) {
        updateSessionCustomerItems(activeCustomerId, resolveItems(itemsOrUpdater, currentItems));
      }
      return;
    }
    setLocalDateOrderTemplates(prev => {
      let changed = false;
      const next = prev.map(cust => {
        if (cust.id !== activeCustomerId) return cust;
        const nextItems = resolveItems(itemsOrUpdater, cust.items ?? EMPTY_ITEMS);
        const totalPrice = getItemsTotalPrice(nextItems);
        if (cust.items === nextItems && cust.totalPrice === totalPrice) return cust;
        changed = true;
        return { ...cust, items: nextItems, totalPrice };
      });
      return changed ? next : prev;
    });
  }, [user, activeCustomerId, currentItems, updateSessionCustomerItems, setLocalDateOrderTemplates]);

  const stableOnOrderCreated = useCallback(() => {
  }, []);

  const extractOrderId = useCallback((customerId: string): number | null => {
    const match = customerId.match(/^order_(\d+)$/);
    return match ? Number(match[1]) : null;
  }, []);

  // 부분입금 자동 전환 reconcile(스펙 §16.10) — 가격 뮤테이션·배송 override 저장 성공 후
  // 해당 주문 status만 서버에서 재조회해 허브에 좁게 반영. 도메인 로직은 훅에 격리.
  // begin/endStatusWrite는 명시적 status 라이터(상태 변경·킵 되돌림·합배송 과거주문)에 barrier로
  // 주입 — 쓰기 진행 중 reconcile 착수를 보류시켜 명시적 변경이 옛 status로 되돌려지는 것 차단.
  const { reconcileOrderStatus, beginStatusWrite, endStatusWrite } = useOrderStatusReconcile({
    updateDateOrderTemplates: setLocalDateOrderTemplates,
  });

  const setShippingFeeOverride = useCallback((value: number | null) => {
    if (activeCustomerId) {
      if (user) {
        setLocalDateOrderTemplates(prev =>
          prev.map(cust =>
            cust.id === activeCustomerId ? { ...cust, shippingFeeOverride: value } : cust
          )
        );
        if (activeCustomerId.startsWith('order_')) {
          const orderId = extractOrderId(activeCustomerId);
          if (orderId) {
            console.log(`TODO: Persist shippingFeeOverride ${value} for order ${orderId}`);
          }
        }
      } else {
        console.warn(`Non-user: Setting shippingFeeOverride for ${activeCustomerId} to ${value}. This change is not persisted for session customers without updates to useCustomerTemplates hook.`);
      }
    }
  }, [user, activeCustomerId, setLocalDateOrderTemplates, extractOrderId]);

  const {
    addItem: originalAddItem,
    addRecentItem: originalAddRecentItem,
    totalAmount,
    calculatedShippingFee,
    paymentAmount,
    shippingFee, setShippingFee,
    freeShippingThreshold, setFreeShippingThreshold,
    template,
    setTemplate: __setTemplateInternalPS,
    memberSetting, setMemberSetting,
    memberSettings, setMemberSettings,
    resetOrder,
    removeItemByIndex: removeItemFromOrder,
    updateItem: updateItemInOrder,
    generateMessage,
  } = usePurchaseState(
    user,
    pseudoUserId,
    currentItems,
    handleItemsChangeAndPersist,
    addOrUpdateItemInCatalog,
    activeCustomerId,
    activeCustomer?.nickname || null,
    selectedDate,
    stableOnOrderCreated,
    user
      ? (activeCustomer ? (activeCustomer as CustomerTemplate).shippingFeeOverride : null)
      : (activeCustomerId ? guestShippingOverrides[activeCustomerId] ?? null : null),
    setShippingFeeOverride,
    reconcileOrderStatus
  );

  const messageTemplates = useMessageTemplates();

  const [displayTemplate, _setDisplayTemplate] = useState<string>(template);

  // activeTemplate.content가 바뀔 때마다 displayTemplate + usePurchaseState.template을 함께 동기화.
  // 한 effect 안에서 둘 다 set하여 종래의 2단계 chain (template→useEffect→displayTemplate)에서
  // 발생하던 race / 누락을 차단. (탭 전환, 새 템플릿 생성, 삭제 후 새 active 등 모든 경로 커버)
  useEffect(() => {
    const content = messageTemplates.activeTemplate?.content;
    if (content == null) return;
    _setDisplayTemplate(content);
    __setTemplateInternalPS(content);
  }, [messageTemplates.activeTemplate?.id, messageTemplates.activeTemplate?.content, __setTemplateInternalPS]);

  // usePurchaseState.template이 외부 경로(예: handleTemplateChangeForMessageSection)로 바뀔 때
  // displayTemplate에 반영. 위 effect와 dependency 다름 (활성 템플릿 변경이 아닌 직접 typing 등).
  useEffect(() => {
    _setDisplayTemplate(template);
  }, [template]);

  const {
    addItem,
    addRecentItem: handleRecentItemAdd
  } = useCustomerItemOperations({
    user,
    pseudoUserId,
    activeCustomerId,
    activeCustomer: activeCustomer || null,
    currentItems: currentItems,
    setCurrentItems: handleItemsChangeAndPersist,
    updateDateOrderTemplates: setLocalDateOrderTemplates,
    selectedDate,
    originalAddItem,
    originalAddRecentItem,
    setActiveCustomerId: setActiveCustomerIdState,
    addOrUpdateItemInCatalog,
    markOrderIdPending: (orderId: string) => pendingCreatedOrderIdsRef.current.add(orderId),
    onOrderMutated: reconcileOrderStatus,
  });

  // 담기 계열 액션 후 재고 뱃지 재조회(오너 지시 — 실시간 반영). 원 핸들러의 persist가
  // resolve된 "뒤에" 재조회해야 서버가 새 주문을 본 상태로 재파생한다(read-your-writes).
  // addRecentItem/addItem 둘 다 orderService 쓰기를 await 후 resolve하므로 이 순서가 성립.
  // 수량 ±·삭제 등 나머지 변이 경로는 mount+visibilitychange가 커버(디바운스 없는 단발 호출,
  // 연타는 훅의 latest-wins seq가 정리). 재조회는 게스트 no-op + 서버 EXISTS 가드로 마스터 0
  // 셀러는 1~3ms라 무조건 호출해도 무해 — 실패-복구(초기 로드 실패 후 담기)까지 공짜로 커버.
  const handleRecentItemAddWithStockRefresh = useCallback(async (item: Item) => {
    await handleRecentItemAdd(item);
    refreshQuickAdd();
  }, [handleRecentItemAdd, refreshQuickAdd]);

  const handleAddItem = useCallback(async (payload: NewItem): Promise<boolean> => {
    const ok = await addItem(payload);
    refreshQuickAdd();
    return ok;
  }, [addItem, refreshQuickAdd]);

  const [showTemplateEditorInHome, setShowTemplateEditorInHome] = useState(false);
  const [showShippingSettingsInHome, setShowShippingSettingsInHome] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [customerIdToDelete, setCustomerIdToDelete] = useState<string | null>(null);
  const openDeleteConfirm = useCallback((id: string) => {
    setCustomerIdToDelete(id);
    setIsDeleteConfirmOpen(true);
  }, []);

  const titleStyle = "text-lg font-semibold text-[#333d4b]"
  const baseMessage = generateMessage(undefined)
  const activeCustomerName = activeCustomer?.nickname
  const generatedMessage = baseMessage

  const updateActiveTemplate = messageTemplates.updateActiveTemplate;
  const handleRenameActiveTemplate = useCallback(async (newName: string) => {
    await updateActiveTemplate({ name: newName });
  }, [updateActiveTemplate]);

  const handleUpdateActiveTemplateContent = useCallback(async (content: string) => {
    await updateActiveTemplate({ content });
  }, [updateActiveTemplate]);

  const handleTemplateChangeForMessageSection = useCallback((newTemplate: string) => {
    _setDisplayTemplate(newTemplate);
    if (!user && activeCustomerId && updateSessionTemplate) {
      updateSessionTemplate(activeCustomerId, newTemplate);
    } else if (user && activeCustomerId) {
      setLocalDateOrderTemplates(prev =>
        prev.map(cust => cust.id === activeCustomerId ? { ...cust, template: newTemplate } : cust)
      );
      __setTemplateInternalPS(newTemplate);
    }
  }, [user, activeCustomerId, updateSessionTemplate, setLocalDateOrderTemplates, __setTemplateInternalPS, _setDisplayTemplate]);

  useEffect(() => {
    const currentDisplayTemplates = user ? localDateOrderTemplates : sessionCustomerTemplates;
    if (user && ordersLoading) {
      return;
    }
    if (currentDisplayTemplates && currentDisplayTemplates.length > 0) {
      const activeCustomerExistsInCurrentList = currentDisplayTemplates.some(c => c.id === activeCustomerId);
      if (!activeCustomerId || !activeCustomerExistsInCurrentList) {
        const newActiveId = currentDisplayTemplates[0].id;
        setActiveCustomerIdState(newActiveId);
      }
    } else if (user && (!currentDisplayTemplates || currentDisplayTemplates.length === 0)) {
      const defaultMemberCustomer = getDefaultCustomer();
      if (defaultMemberCustomer) {
        const newActiveId = defaultMemberCustomer.id;
        setActiveCustomerIdState(newActiveId);
      } else {
        setActiveCustomerIdState(null);
      }
    } else if (!user && (!currentDisplayTemplates || currentDisplayTemplates.length === 0)) {
      setActiveCustomerIdState(null);
    }
  }, [user, localDateOrderTemplates, sessionCustomerTemplates, ordersLoading, getDefaultCustomer, activeCustomerId]);

  // 직전 activeCustomerId 추적 — customer 전환과 customer 필드 update를 구분.
  // 사용자가 textarea를 비우면 customer.template = "" 이 되고 activeCustomer 객체가
  // 새로 생성되어 useEffect가 재실행됨. 이때 fallback이 OLD content로 displayTemplate을
  // 복원해 textarea가 reverted되는 버그가 있었음. customer가 실제로 SWITCH될 때만
  // fallback 적용하여 같은 customer 내 편집은 그대로 유지.
  const prevActiveCustomerIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const activeContent = messageTemplates.activeTemplate?.content;
    const currentActiveCust = activeCustomer;

    // (items는 activeCustomer.items에서 직접 derive — 별도 sync 불필요. A2에서 제거.)

    // template은 customer가 실제로 바뀐 경우에만 fallback 적용.
    const customerSwitched = prevActiveCustomerIdRef.current !== activeCustomerId;
    prevActiveCustomerIdRef.current = activeCustomerId;
    if (!customerSwitched) return;

    // Fallback 순서:
    //   1) customer.template — 사용자가 이 고객 전용으로 명시 customize한 텍스트 (보존).
    //   2) active.content — 다중 템플릿 도입 후의 새 default (memberSetting 대신).
    //   3) memberSetting.messageTemplate — legacy fallback.
    if (currentActiveCust) {
      _setDisplayTemplate(currentActiveCust.template || activeContent || memberSetting.messageTemplate || "");
    } else if (!activeCustomerId) {
      _setDisplayTemplate(activeContent || memberSetting.messageTemplate || "");
    }
  }, [activeCustomerId, activeCustomer, memberSetting.messageTemplate, messageTemplates.activeTemplate?.content, _setDisplayTemplate]);

  // URL 딥링크(?date, ?nickname) 진입 처리. location은 본 훅이 소유(useHomeUrlParams).
  // customerTemplates는 Home 소유 live hub → 인자로 주입(snapshot 금지).
  const { goToToday } = useHomeUrlParams({ setSelectedDate, customerTemplates, setActiveCustomerIdState, user, isAuthLoading });

  // ActiveCustomerColumn에 props로 넘기는 callback — 매 렌더 새 참조가 되면 memo bail-out이
  // 깨지므로 useCallback으로 안정화. ref 접근만 하고 외부 클로저 캡처가 없어 deps는 빈 배열.
  const focusInlineMemoEnd = useCallback(() => {
    const el = inlineMemoRef.current;
    if (!el) return;
    const apply = () => {
      const end = el.value.length;
      el.focus();
      try {
        el.setSelectionRange(end, end);
      } catch { }
    };
    // Two RAFs to ensure after React paint and layout
    requestAnimationFrame(() => requestAnimationFrame(apply));
  }, []);

  useLayoutEffect(() => {
    if (isEditingMemo) {
      focusInlineMemoEnd();
    }
  }, [isEditingMemo, memoInput]);

  useEffect(() => {
    if (!user && sessionCustomerTemplates.length > 0 &&
      sessionCustomerTemplates[0].nickname === DEFAULT_CUSTOMER_NICKNAME &&
      !sessionCustomerTemplates[0].template && updateSessionTemplate) {
      const defaultTemplateToSet = memberSetting.messageTemplate || displayTemplate;
      updateSessionTemplate(sessionCustomerTemplates[0].id, defaultTemplateToSet);
      _setDisplayTemplate(defaultTemplateToSet);
    }
  }, [user, sessionCustomerTemplates, memberSetting.messageTemplate, displayTemplate, updateSessionTemplate, _setDisplayTemplate]);

  const handleDateChange = useCallback((date: Date) => {
    setSelectedDate(date);
  }, [setSelectedDate]);

  // 고객 CRUD 도메인(add/remove/edit)은 useCustomerCrud로 분리(P15a remove + P15b add/edit).
  // 핸들러 body·dep 배열은 바이트 그대로 훅으로 이동. 인덱스 계약/롤백/setTemplate 폴백/pendingRef = 훅 JSDoc 참조.
  // ★ pendingCreatedOrderIdsRef는 Home 소유 단일 인스턴스(L187)를 주입(§6 게이트4 — 훅 자체 ref 생성 금지).
  //   handleCustomerMemoChange는 Home 잔류(memo-editing hazard, 범위 밖).
  const { handleCustomerAddRequest, handleCustomerRemoveRequest, handleCustomerEditRequest } = useCustomerCrud({
    user,
    localDateOrderTemplates,
    activeCustomerId,
    displayTemplate,
    customerTemplates,
    setLocalDateOrderTemplates,
    setActiveCustomerIdState,
    _setDisplayTemplate,
    getDefaultCustomer,
    removeOrderAndUpdateTemplates,
    removeLocalCustomerOnly,
    refreshOrders,
    messageTemplates,
    memberSetting,
    extractOrderId,
    removeSessionCustomer,
    selectedDate,
    pendingCreatedOrderIdsRef,
    addSessionCustomer,
    updateSessionCustomerNickname,
    updateDefaultCustomerNickname,
  });

  const handleCustomerMemoChange = useCallback(async (customerId: string, newMemo: string): Promise<boolean> => {
    if (!user) {
      toast.warning("로그인 후 메모 기능을 사용할 수 있습니다.");
      return false;
    }

    if (newMemo.length > 300) {
      alert("메모는 300자 이내로 작성해주세요.");
      return false;
    }

    setLocalDateOrderTemplates(prev =>
      prev.map(cust =>
        cust.id === customerId ? { ...cust, memo: newMemo } as CustomerTemplate : cust
      )
    );

    const orderId = extractOrderId(customerId);
    if (orderId) {
      try {
        await orderService.updateOrderMemo(orderId, newMemo);
        toast.success(`메모가 저장되었습니다.`);
      } catch (error) {
        logError(`[Home.tsx] DB 메모 업데이트 실패 (orderId ${orderId}):`, error);
        toast.error("메모 저장에 실패했습니다.");
        return false;
      }
    } else {
      toast.info(`임시 고객의 메모가 앱에 저장되었습니다.`);
    }
    return true;
  }, [user, setLocalDateOrderTemplates, extractOrderId]);

  // ActiveCustomerColumn에 props로 넘기는 callback — memo bail-out 유지를 위해 안정 참조 필요.
  const handleMemoKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (activeCustomerId) {
        handleCustomerMemoChange(activeCustomerId, memoInput);
      }
      setIsEditingMemo(false);
      setIsMemoPopoverOpen(false);
      setIsMemoPopoverOpenCompact(false);
      e.currentTarget.blur();
    }
  }, [activeCustomerId, memoInput, handleCustomerMemoChange]);

  const handleRemoveItemByIndex = useCallback(async (index: number) => {
    if (index < 0 || index >= currentItems.length) return;
    const originalItems = currentItems;
    const updatedItems = currentItems.filter((_, i) => i !== index);
    // 낙관적 갱신: chokepoint가 source(items + totalPrice)를 갱신.
    handleItemsChangeAndPersist(updatedItems);
    // DB 영속은 order_ 고객만. 실패 시 chokepoint로 롤백.
    if (user && activeCustomerId && activeCustomerId.startsWith('order_')) {
      const orderId = extractOrderId(activeCustomerId);
      if (orderId) {
        try {
          await removeItemFromOrder(index);
        } catch (error) {
          logError("[Home.tsx] DB 상품 삭제 실패:", error);
          alert("DB에서 상품 삭제에 실패했습니다. UI를 되돌립니다.");
          handleItemsChangeAndPersist(originalItems);
        }
      }
    }
    // 재고 뱃지 재조회(codexloop 델타 P2 수용): 담기만 라이브면 옆의 삭제/수량 조작에서 뱃지가
    // 멈춰 보이는 게 더 혼란. persist(성공·롤백 무관) 정리 후 서버 진실 재조회 — 실패 롤백
    // 케이스에도 refetch는 항상 옳은 값을 가져온다. 비-order_ 경로는 값-동등 → 렌더 0.
    refreshQuickAdd();
  }, [user, activeCustomerId, currentItems, handleItemsChangeAndPersist, removeItemFromOrder, extractOrderId, refreshQuickAdd]);

  const handleUpdateItem = useCallback(async (index: number, data: Partial<Item>) => {
    if (index < 0 || index >= currentItems.length) return;

    const originalItems = currentItems;
    const updatedItems = originalItems.map((item, i) => (i === index ? { ...item, ...data } : item));

    // 낙관적 갱신: chokepoint가 source(items + totalPrice)를 갱신.
    handleItemsChangeAndPersist(updatedItems);

    try {
      // This now handles DB updates for both logged-in and non-logged-in users
      await updateItemInOrder(index, data);
    } catch (error) {
      logError("[Home.tsx] 상품 정보 업데이트 실패:", error);
      // 실패 시 chokepoint로 롤백.
      handleItemsChangeAndPersist(originalItems);
    }
    // 재고 뱃지 재조회 — 수량 ±(handleUpdateItemQuantity 경유)·이름/가격 수정 모두 이 경로.
    refreshQuickAdd();
  }, [currentItems, handleItemsChangeAndPersist, updateItemInOrder, refreshQuickAdd]);

  const handleUpdateItemQuantity = useCallback((index: number, newQuantity: number) => {
    handleUpdateItem(index, { quantity: newQuantity });
  }, [handleUpdateItem]);

  // 동일 상품 일괄 수정 도메인(편집 행 opt-in 체크박스)은 useSameProductBulkEdit로 분리.
  // 체크 해제(기본) 저장은 기존 handleUpdateItem 경로 그대로 — 이 훅은 체크 시에만 발동.
  const { getBulkApplyTargets, handleUpdateItemWithSameProducts } = useSameProductBulkEdit({
    user,
    activeCustomerId,
    customerTemplates,
    currentItems,
    updateItemInOrder,
    refreshOrders,
    extractOrderId,
  });

  // 동일 상품 일괄 수정도 수량 변경 = 예약 변화 — persist resolve 후 재고 뱃지 재조회.
  const handleUpdateItemWithSameProductsAndStockRefresh = useCallback(async (index: number, changes: Partial<Item>) => {
    await handleUpdateItemWithSameProducts(index, changes);
    refreshQuickAdd();
  }, [handleUpdateItemWithSameProducts, refreshQuickAdd]);

  // currentOrderStatus / isOrderKept: activeCustomer에서 직접 derive.
  // (이전: 별도 state + activeCustomer 변경 effect로 미러 → 클릭당 paint 후 추가 렌더 발생.)
  // 모든 변경 핸들러가 이미 source(localDateOrderTemplates)를 갱신하므로 derive가 동작 동등.
  // status/keep UI(OrderControls)는 로그인 사용자에게만 노출되며, 그 경우 customerTemplates===localDateOrderTemplates.
  const currentOrderStatus: OrderStatus = activeCustomer?.status ?? "미지정";
  const isOrderKept: boolean = activeCustomer?.is_keep ?? false;

  // 입금 계좌 연동 상태(스펙 D10): '부분 입금' 선택지 노출 게이트. 'loading'/'unknown'은 미노출(보수적 기본값).
  const { status: depositAccountStatus } = useDepositAccountStatus();
  const statusOptions = useMemo(
    () => selectableOrderStatuses(depositAccountStatus === 'linked'),
    [depositAccountStatus],
  );

  // 킵(is_keep) 토글 도메인은 useOrderKeepActions로 분리(P16A). 두 핸들러 body는 바이트 그대로 이동.
  // dep 배열 의도적 상이(read=customerTemplates / write=setLocalDateOrderTemplates 비대칭) → 훅 내부 주석 참고.
  const { handleKeepOrderChange, handleKeepToggle } = useOrderKeepActions({
    currentOrderStatus,
    activeCustomer,
    activeCustomerId,
    customerTemplates,
    setLocalDateOrderTemplates,
    extractOrderId,
    onStatusWriteStart: beginStatusWrite,
    onStatusWriteEnd: endStatusWrite,
  });

  // 합배송 모달은 CombinedShippingModalContainer로 분리(P11). 열림/관련주문/소스 state는 컨테이너 소유 →
  // 모달 오픈이 Home 본체를 재렌더하지 않는다. Home은 ref.open()으로 열기만 한다.
  const combinedShippingRef = useRef<CombinedShippingModalHandle>(null);
  // window.confirm 대체 다이얼로그(출고 경고·배치 일괄 변경 확인) — 동일 컨테이너 패턴, 단일 인스턴스 주입.
  const confirmDialogRef = useRef<ConfirmDialogHandle>(null);
  // 다른 배송비 드래프트 캐시 — MessageTemplateSection이 lg 메시지 탭 전환으로 리마운트돼도
  // 미저장 입력을 보존(규율 6: 공유 ref는 Home이 단일 인스턴스 주입). base=드래프트 기반 커밋본 identity.
  const shippingPresetDraftsRef = useRef<{ base: number[]; drafts: string[] } | null>(null);

  // 토글에는 저장된 프리셋만 노출 — 설정 폼의 미저장 드래프트가 주문 화면에 새는 것 차단(codex R1 P1)
  const committedShippingPresets = useMemo(
    () => memberSettings.find((s) => s.id === memberSetting.id)?.shippingFeePresets ?? [],
    [memberSettings, memberSetting.id],
  );

  // 배송비 모드(auto | jeju | free) 도메인은 useShippingFeeMode로 분리(P12).
  // guestShippingOverrides는 Home이 소유(공유 hub) → live 참조로 주입. usePurchaseState 이후 호출(jejuShippingFee 의존).
  const {
    shippingMode,
    handleShippingModeChange,
    setShippingFeeOverridePersisted,
  } = useShippingFeeMode({
    user,
    activeCustomer,
    activeCustomerId,
    jejuShippingFee: memberSetting.jejuShippingFee,
    shippingPresets: committedShippingPresets,
    guestShippingOverrides,
    setGuestShippingOverrides,
    setShippingFeeOverride,
    extractOrderId,
    onOrderMutated: reconcileOrderStatus,
  });

  const {
    proceedWithStatusUpdate,
    handleBatchStatusChange,
    handleOrderStatusChange,
  } = useOrderStatusActions({
    user,
    selectedDate,
    activeCustomer,
    activeCustomerId,
    customerTemplates,
    selectedCustomerIds,
    setSelectedCustomerIds,
    setIsBatchProcessing,
    setLocalDateOrderTemplates,
    extractOrderId,
    combinedShippingRef,
    confirmDialogRef,
    onStatusWriteStart: beginStatusWrite,
    onStatusWriteEnd: endStatusWrite,
  });

  // 결제수단(현금/카드) 기재 도메인 — opt-in(member_preference) 셀러에게만 토글 렌더.
  // customerTemplates는 useOrderStatusActions와 동일한 committed-state 변수 주입(선례),
  // setLocalDateOrderTemplates는 live 참조(snapshot 금지).
  const { recordPaymentMethod } = useMemberPreference({ user });

  // 거파(블랙리스트) 허브 — 도메인 로직은 훅에, Home은 배선만 (스펙 §6)
  const { blacklistEntries, blacklistStatus, blacklistByNickname, registerBlacklist, unregisterBlacklist } = useBlacklist({ user });
  const activeBlacklistEntry = activeCustomer?.nickname
    ? blacklistByNickname.get(activeCustomer.nickname.trim())
    : undefined;

  const { handlePaymentMethodChange } = usePaymentMethodActions({
    user,
    customerTemplates,
    setLocalDateOrderTemplates,
    extractOrderId,
  });

  const {
    isMoveOrderDateDialogOpen,
    moveOrderDate,
    moveDateMode,
    setMoveOrderDate,
    openMoveOrderDateDialog,
    handleMoveOrderDateOpenChange,
    handleConfirmMoveOrderDate,
  } = useMoveOrderDate({
    user,
    selectedDate,
    activeCustomer,
    activeCustomerId,
    selectedCustomerIds,
    setSelectedCustomerIds,
    isBatchProcessing,
    setIsBatchProcessing,
    extractOrderId,
    refreshOrders,
  });

  const handleViewOrderDetails = useCallback((customerId: string) => {
    setActiveCustomerIdState(customerId);
    setViewMode('detail');
  }, [setActiveCustomerIdState]);

  const handleShowMemo = useCallback((customer: CustomerTemplate) => {
    setMemoToShow({ nickname: customer.nickname, memo: customer.memo || "" });
  }, []);

  const handleCloseMemoDialog = useCallback(() => setMemoToShow(null), []);

  const handleConfirmDeleteCustomer = useCallback(async () => {
    if (customerIdToDelete) {
      await handleCustomerRemoveRequest(customerIdToDelete, { skipConfirm: true });
    }
    setIsDeleteConfirmOpen(false);
    setCustomerIdToDelete(null);
  }, [customerIdToDelete, handleCustomerRemoveRequest]);

  // xl/lg/mobile 3벌의 <ActiveCustomerColumn> prop 블록은 variant만 다르고 나머지 43개 prop이 동일.
  // Home scope in-render 클로저로 PIN(module-scope wrapper/useMemo 금지 = remount/memo death).
  const renderProductColumn = (variant: ActiveCustomerColumnVariant) => (
    <ActiveCustomerColumn
      variant={variant}
      titleStyle={titleStyle}
      user={user}
      activeCustomer={activeCustomer}
      activeCustomerId={activeCustomerId}
      currentItems={currentItems}
      totalAmount={totalAmount}
      calculatedShippingFee={calculatedShippingFee}
      paymentAmount={paymentAmount}
      handleItemsChangeAndPersist={handleItemsChangeAndPersist}
      handleAddItem={handleAddItem}
      handleRecentItemAdd={handleRecentItemAddWithStockRefresh}
      handleRemoveItemByIndex={handleRemoveItemByIndex}
      handleUpdateItemQuantity={handleUpdateItemQuantity}
      handleUpdateItem={handleUpdateItem}
      getBulkApplyTargets={getBulkApplyTargets}
      handleUpdateItemWithSameProducts={handleUpdateItemWithSameProductsAndStockRefresh}
      resetOrder={resetOrder}
      isOrderKept={isOrderKept}
      currentOrderStatus={currentOrderStatus}
      handleKeepOrderChange={handleKeepOrderChange}
      handleOrderStatusChange={handleOrderStatusChange}
      statusOptions={statusOptions}
      shippingMode={shippingMode}
      handleShippingModeChange={handleShippingModeChange}
      jejuShippingFee={memberSetting.jejuShippingFee}
      shippingPresets={committedShippingPresets}
      paymentMethodEnabled={recordPaymentMethod}
      handlePaymentMethodChange={handlePaymentMethodChange}
      openMoveOrderDateDialog={openMoveOrderDateDialog}
      isEditingMemo={isEditingMemo}
      setIsEditingMemo={setIsEditingMemo}
      memoInput={memoInput}
      setMemoInput={setMemoInput}
      isMemoPopoverOpen={isMemoPopoverOpen}
      setIsMemoPopoverOpen={setIsMemoPopoverOpen}
      isMemoPopoverOpenCompact={isMemoPopoverOpenCompact}
      setIsMemoPopoverOpenCompact={setIsMemoPopoverOpenCompact}
      handleCustomerMemoChange={handleCustomerMemoChange}
      handleMemoKeyDown={handleMemoKeyDown}
      focusInlineMemoEnd={focusInlineMemoEnd}
      inlineMemoRef={inlineMemoRef}
      popoverMemoRef={popoverMemoRef}
      productCatalog={productCatalog}
      isCatalogLoading={isCatalogLoading}
      deleteCatalogItem={deleteCatalogItem}
      clearCatalog={clearCatalog}
      inventoryMasterItems={inventoryMasterItems}
      inventoryStockByNorm={inventoryStockByNorm}
      onboardingActive={onboarding.isActive}
      onboardingStep={onboarding.currentStep}
      blacklistEntry={activeBlacklistEntry}
      blacklistEntries={blacklistEntries}
      blacklistStatus={blacklistStatus}
      onBlacklistRegister={registerBlacklist}
      onBlacklistUnregister={unregisterBlacklist}
      openLoginModal={openLoginModal}
    />
  );

  return (
    <TooltipProvider>
      <>
        <div className="min-h-screen bg-gray-100">
          <div className="mx-auto w-full max-w-screen-2xl px-3 sm:px-4 lg:px-6 py-4">
            {/* View Mode Toggle (로그인 사용자에게만 표시) */}
            {user && <ViewModeToggle viewMode={viewMode} onChange={handleViewModeChange} />}

            {/* 오늘이 아닌 날짜 경고. detail/batch 두 뷰 모두 덮도록 뷰 분기 바깥에 둔다
                (batch 뷰에는 날짜 표시가 전혀 없어 고착을 알아챌 단서가 없었다). */}
            {user && <OrderDateNotice selectedDate={selectedDate} onGoToday={goToToday} />}

            {/* Main Content: 3-Panel Grid on xl, 2-Panel with Tabs on lg */}
            {viewMode === 'detail' ? (
              <div className="grid gap-3 grid-cols-1 lg:grid-cols-[clamp(240px,24vw,340px),1fr] xl:grid-cols-[clamp(260px,24vw,360px),clamp(300px,34vw,512px),minmax(420px,1fr)]">
                {/* LEFT: Customer */}
                <aside className="bg-white rounded-xl border lg:h-[calc(100vh-120px)] md:sticky md:top-4 lg:overflow-y-auto p-4">
                  {(user && ordersLoading) ? (
                    <div className="bg-white rounded-2xl p-4 shadow-sm">
                      <div className="text-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
                        <p className="mt-2 text-gray-500">주문 내역을 불러오는 중...</p>
                      </div>
                    </div>
                  ) : (
                    <DepositIncomingProvider
                      selectedDate={selectedDate}
                      refreshOrders={refreshOrders}
                      gridOrderIds={gridOrderIds}
                    >
                      <CustomerTemplateManager
                        activeCustomerId={activeCustomerId}
                        customerTemplates={customerTemplates}
                        onCustomerSelect={setActiveCustomerIdState}
                        onCustomerAdd={handleCustomerAddRequest}
                        onCustomerRemove={openDeleteConfirm}
                        onCustomerEdit={handleCustomerEditRequest}
                        selectedDate={selectedDate}
                        setSelectedDate={setSelectedDate}
                        isDatePickerEnabled={!!user}
                        onDateChange={handleDateChange}
                        titleStyle={titleStyle}
                        onDownloadSettlement={handleDownloadSettlement}
                        hasPaidOrders={hasPaidOrders}
                        onOpenShippingModal={openShippingModal}
                        ordersWithShippingInfo={ordersWithShippingInfo}
                        incomingSlot={depositIncomingSlot}
                        statusOptions={statusOptions}
                        blacklistByNickname={blacklistByNickname}
                      />
                    </DepositIncomingProvider>
                  )}
                </aside>

                {/* MIDDLE: Product (xl only) */}
                {isXl && <main className="bg-white rounded-xl border h-[calc(100vh-120px)] overflow-y-auto p-4">
                  {renderProductColumn("xl")}
                </main>}

                {/* Onboarding Tour */}
                <OnboardingTour
                  isActive={onboarding.isActive}
                  currentStep={onboarding.currentStep}
                  isCompleted={onboarding.isCompleted}
                  onNext={onboarding.nextStep}
                  onPrev={onboarding.prevStep}
                  onSkip={onboarding.skipTour}
                  onComplete={onboarding.completeTour}
                />

                {/* RIGHT: Message */}
                <section id="message-section" className="bg-white rounded-xl border h-[calc(100vh-120px)] overflow-y-auto p-4">
                  {/* xl: show message content only */}
                  {isXl && <div>
                    <MessageHeaderToolbar
                      titleStyle={titleStyle}
                      user={user}
                      memberSetting={memberSetting}
                      activeCustomer={activeCustomer}
                      showShippingSettingsInHome={showShippingSettingsInHome}
                      setShowShippingSettingsInHome={setShowShippingSettingsInHome}
                      showTemplateEditorInHome={showTemplateEditorInHome}
                      setShowTemplateEditorInHome={setShowTemplateEditorInHome}
                    />
                    <MessageTemplateColumn
                      template={displayTemplate}
                      setTemplate={handleTemplateChangeForMessageSection}
                      generatedMessage={generatedMessage}
                      resetOrder={resetOrder}
                      activeCustomerName={activeCustomerName}
                      hasProducts={currentItems.length > 0}
                      memberSetting={memberSetting}
                      setMemberSetting={setMemberSetting}
                      shippingFee={shippingFee}
                      setShippingFee={setShippingFee}
                      freeShippingThreshold={freeShippingThreshold}
                      setFreeShippingThreshold={setFreeShippingThreshold}
                      openLoginModal={openLoginModal}
                      memberSettings={memberSettings}
                      setMemberSettings={setMemberSettings}
                      presetDraftsRef={shippingPresetDraftsRef}
                      showTemplateEditorProp={showTemplateEditorInHome}
                      showShippingSettingsProp={showShippingSettingsInHome}
                      subscriptionPlanId={subscriptionPlan?.id}
                      calculatedShippingFee={calculatedShippingFee}
                      messageTemplates={messageTemplates}
                      onRenameActive={handleRenameActiveTemplate}
                      onUpdateActiveContent={handleUpdateActiveTemplateContent}
                    />
                  </div>}

                  {/* lg (but not xl): Tabs for Product/Message */}
                  {isLg && !isXl && <div>
                    <Tabs defaultValue="product" className="w-full">
                      <TabsList className="grid grid-cols-2 w-full">
                        <TabsTrigger value="product">상품</TabsTrigger>
                        <TabsTrigger value="message">메시지</TabsTrigger>
                      </TabsList>
                      <TabsContent value="product" className="mt-4">
                        {renderProductColumn("lg")}
                      </TabsContent>
                      <TabsContent value="message" className="mt-4">
                        {/* Message content reused */}
                        <div>
                          <LgMessageHeaderToolbar
                            titleStyle={titleStyle}
                            user={user}
                            memberSetting={memberSetting}
                            activeCustomer={activeCustomer}
                            showShippingSettingsInHome={showShippingSettingsInHome}
                            setShowShippingSettingsInHome={setShowShippingSettingsInHome}
                            showTemplateEditorInHome={showTemplateEditorInHome}
                            setShowTemplateEditorInHome={setShowTemplateEditorInHome}
                            openMoveOrderDateDialog={openMoveOrderDateDialog}
                            setMemoInput={setMemoInput}
                            setIsEditingMemo={setIsEditingMemo}
                            focusInlineMemoEnd={focusInlineMemoEnd}
                          />
                          <MessageTemplateColumn
                            template={displayTemplate}
                            setTemplate={handleTemplateChangeForMessageSection}
                            generatedMessage={generatedMessage}
                            resetOrder={resetOrder}
                            activeCustomerName={activeCustomerName}
                            hasProducts={currentItems.length > 0}
                            memberSetting={memberSetting}
                            setMemberSetting={setMemberSetting}
                            shippingFee={shippingFee}
                            setShippingFee={setShippingFee}
                            freeShippingThreshold={freeShippingThreshold}
                            setFreeShippingThreshold={setFreeShippingThreshold}
                            openLoginModal={openLoginModal}
                            memberSettings={memberSettings}
                            setMemberSettings={setMemberSettings}
                            presetDraftsRef={shippingPresetDraftsRef}
                            showTemplateEditorProp={showTemplateEditorInHome}
                            showShippingSettingsProp={showShippingSettingsInHome}
                            subscriptionPlanId={subscriptionPlan?.id}
                            calculatedShippingFee={calculatedShippingFee}
                            messageTemplates={messageTemplates}
                            onRenameActive={handleRenameActiveTemplate}
                            onUpdateActiveContent={handleUpdateActiveTemplateContent}
                          />
                        </div>
                      </TabsContent>
                    </Tabs>
                  </div>}

                  {/* small screens: stack Product then Message */}
                  {!isLg && <div>
                    {/* Product */}
                    <div className="mb-6">
                      {renderProductColumn("mobile")}
                    </div>

                    {/* Message */}
                    <div>
                      <MessageHeaderToolbar
                        titleStyle={titleStyle}
                        user={user}
                        memberSetting={memberSetting}
                        activeCustomer={activeCustomer}
                        showShippingSettingsInHome={showShippingSettingsInHome}
                        setShowShippingSettingsInHome={setShowShippingSettingsInHome}
                        showTemplateEditorInHome={showTemplateEditorInHome}
                        setShowTemplateEditorInHome={setShowTemplateEditorInHome}
                      />
                      <MessageTemplateColumn
                        template={displayTemplate}
                        setTemplate={handleTemplateChangeForMessageSection}
                        generatedMessage={generatedMessage}
                        resetOrder={resetOrder}
                        activeCustomerName={activeCustomerName}
                        hasProducts={currentItems.length > 0}
                        memberSetting={memberSetting}
                        setMemberSetting={setMemberSetting}
                        shippingFee={shippingFee}
                        setShippingFee={setShippingFee}
                        freeShippingThreshold={freeShippingThreshold}
                        setFreeShippingThreshold={setFreeShippingThreshold}
                        openLoginModal={openLoginModal}
                        memberSettings={memberSettings}
                        setMemberSettings={setMemberSettings}
                        presetDraftsRef={shippingPresetDraftsRef}
                        showTemplateEditorProp={showTemplateEditorInHome}
                        showShippingSettingsProp={showShippingSettingsInHome}
                        subscriptionPlanId={subscriptionPlan?.id}
                        calculatedShippingFee={calculatedShippingFee}
                        messageTemplates={messageTemplates}
                        onRenameActive={handleRenameActiveTemplate}
                        onUpdateActiveContent={handleUpdateActiveTemplateContent}
                      />
                    </div>
                  </div>}
                </section>
              </div>
            ) : (
              <BatchModeView
                customers={filteredCustomerTemplates}
                selectedCustomerIds={selectedCustomerIds}
                onSelectionChange={setSelectedCustomerIds}
                onBatchStatusChange={handleBatchStatusChange}
                openMoveOrderDateDialog={openMoveOrderDateDialog}
                isProcessing={isBatchProcessing}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                keepFilter={keepFilter}
                onKeepFilterChange={setKeepFilter}
                onDownloadExcel={handleDownloadFilteredExcel}
                statusOptions={statusOptions}
                onViewDetails={handleViewOrderDetails}
                onShowMemo={handleShowMemo}
                onKeepToggle={handleKeepToggle}
                sortOption={sortOption}
                onSortChange={setSortOption}
              />
            )}

            {viewMode === 'detail' && (
              <TotalOrderSummary summary={totalOrderSummary} titleStyle={titleStyle} />
            )}

            {viewMode === 'detail' && (
              <SettingsManager
                user={user}
                memberSettings={memberSettings}
                memberSetting={memberSetting}
                openLoginModal={openLoginModal}
              />
            )}

            <div className="h-20"></div>
          </div>
        </div>
        <RelevantOrdersPanelContainer
          userId={user?.id}
          activeCustomerId={activeCustomerId}
          activeCustomerNickname={activeCustomer?.nickname}
          selectedDate={selectedDate}
          extractOrderId={extractOrderId}
          currentOrderTotal={totalAmount}
          shippingFeeOverride={
            user
              ? (activeCustomer ? (activeCustomer as CustomerTemplate).shippingFeeOverride ?? null : null)
              : (activeCustomerId ? guestShippingOverrides[activeCustomerId] ?? null : null)
          }
          setShippingFeeOverride={setShippingFeeOverridePersisted}
          hasProducts={currentItems.length > 0}
        />
        <CombinedShippingModalContainer
          ref={combinedShippingRef}
          nickname={activeCustomer?.nickname || ''}
          customerTemplates={customerTemplates}
          proceedWithStatusUpdate={proceedWithStatusUpdate}
          refreshOrders={refreshOrders}
          setSelectedCustomerIds={setSelectedCustomerIds}
          onStatusWriteStart={beginStatusWrite}
          onStatusWriteEnd={endStatusWrite}
        />
        <ConfirmDialogContainer ref={confirmDialogRef} />
        {memoToShow && <MemoViewerDialog memoToShow={memoToShow} onClose={handleCloseMemoDialog} />}

        <ShippingModalManager
          ref={shippingModalRef}
          onShippingInfoKnown={handleShippingInfoKnown}
          shippingInfoByOrderId={shippingInfoByOrderId}
        />

        {/* 고객 삭제 확인 다이얼로그 */}
        <DeleteConfirmDialog
          open={isDeleteConfirmOpen}
          onOpenChange={setIsDeleteConfirmOpen}
          customerTemplates={customerTemplates}
          customerIdToDelete={customerIdToDelete}
          onConfirm={handleConfirmDeleteCustomer}
        />

        <MoveOrderDateDialog
          open={isMoveOrderDateDialogOpen}
          onOpenChange={handleMoveOrderDateOpenChange}
          mode={moveDateMode}
          selectedCount={selectedCustomerIds.length}
          date={moveOrderDate}
          onDateChange={setMoveOrderDate}
          isProcessing={isBatchProcessing}
          onConfirm={handleConfirmMoveOrderDate}
        />
      </>
    </TooltipProvider>
  )
}
